import { createFileRoute, useNavigate, useLocation, useParams } from '@tanstack/react-router';
import { useState, useEffect, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Building2, Check, Link2, ArrowRight, ArrowLeftRight, UserPlus, Pencil, X, AlertCircle } from 'lucide-react';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useSetAtom, useAtomValue } from 'jotai';
import { organizationDataAtom, googleAdsAccountAtom, selectedOrganizationsTabAtom } from '@/store';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { GoogleAdAccountSelectionModal } from '@/components/glitch-ui/google-ad-account-selection-modal';
import { MetaAdAccountSelectionModal } from '@/components/glitch-ui/meta-ad-account-selection-modal';
import { InviteMemberModal } from '@/components/settings/team/invite-member-modal';
import { MembersTable } from '@/components/settings/team/member-table';
import { BiilingInfo } from '@/components/settings/billing';
import { GoogleAdsPermissions } from '@/components/settings/organization/google-ads-permissions';
import { usePaymentGate } from '@/hooks/usePaymentGate';
import { PlanType } from '@/lib/types';
import { useCampaignTransferMutation, useOrganizationCurrencyMutation, useOrganizationMutationOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { ApiError } from '@/lib/api-error';
import { useToast } from '@/hooks/useToast';
import { useInvalidateQuery } from '@/hooks/useInvalidateQuery';
import { useQuery } from '@tanstack/react-query';
import { fetchCampaigns } from '@/lib/apis';
import { CAMPAIGN_STATE, getCampaignType, getCampaignTypeLabel } from '@/lib/types';
import type { OrganizationDataWithMembership, CampaignData } from '@/lib/types';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import googleAds from '/google-ads.svg';
import metaLogo from '/meta.png';

// A refused currency save says why (#6828): a 403 is a reader who is neither the owner nor an admin, and any
// other refusal carries the server's own reason (a currency that is not the Google Ads account's, say).
function currencySaveFailure(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) return 'Only the owner or an admin can change the currency.';
  return error instanceof Error ? error.message : 'Failed to update currency';
}

export const Route = createFileRoute('/_authenticated/_layout/organizations/')({
  component: OrganizationsPage,
  staticData: {
    title: 'Organizations',
    icon: Building2,
  },
});

import { CURRENCY_OPTIONS, SHORTCUT_CURRENCIES } from '@/lib/currencies';
import { BusinessAiConfiguration } from '@/components/organizations/business-ai-configuration';

const TAB_IDS = ['overview', 'members', 'providers', 'configuration', 'billing', 'transfer'] as const;

export function OrganizationsPage() {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();
  // Get orgId from URL params (works for /organizations/$orgId route)
  const params = useParams({ strict: false }) as { orgId?: string };
  const { organizations, organizationData, switchOrganization, isSwitching } = useOrganizationInitialization();
  const [isGoogleAdsModalOpen, setIsGoogleAdsModalOpen] = useState(false);
  const [isMetaModalOpen, setIsMetaModalOpen] = useState(false);
  // Current-org Meta integration status — the same source the switch modal and user menu read,
  // so the overview Meta row stays in sync.
  const { data: metaIntegration } = useQuery(metaIntegrationStatusQueryOptions());
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  // Tell-on-click validation (#5857): Save stays enabled for an empty name; clicking it marks the field.
  const [editedNameError, setEditedNameError] = useState<string | undefined>(undefined);

  // Currency state
  const [pendingCurrency, setPendingCurrency] = useState<string | null>(null);
  const [currencySaveSuccess, setCurrencySaveSuccess] = useState(false);
  const [currencySaveError, setCurrencySaveError] = useState<string | null>(null);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);

  // Transfer tab state
  const [targetOrganizationId, setTargetOrganizationId] = useState<string>('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([]);

  const setOrganizationData = useSetAtom(organizationDataAtom);
  const transferMutation = useCampaignTransferMutation();
  const updateOrganization = useOrganizationMutationOptions();
  const updateCurrency = useOrganizationCurrencyMutation();
  const invalidateQuery = useInvalidateQuery();
  const { toast } = useToast();
  const { symbol } = useCurrencySymbol();
  const { PaymentGate, requirePayment } = usePaymentGate({ source: 'organization_upgrade' });

  // Auto-suggest Google Ads currency when org has none set
  const suggestedCurrency = !organizationData?.default_currency && googleAdsAccount?.currency ? googleAdsAccount.currency : null;

  // The value the currency dropdown shows. Save persists exactly this value: the
  // Select never fires onValueChange when the already-displayed option is re-picked,
  // so a save path gated on pendingCurrency alone silently no-ops on a fresh org
  // where only the EUR display fallback is in effect.
  const displayedCurrency = pendingCurrency ?? organizationData?.default_currency ?? suggestedCurrency ?? 'EUR';

  // A SET org currency that does not match the connected Google Ads account.
  // This must surface an inline error and hold the user (via the unsaved-changes
  // guard) until they verify/match the currency — they cannot progress past it.
  const currencyMismatch =
    !!googleAdsAccount?.currency
    && !!organizationData?.default_currency
    && organizationData.default_currency !== googleAdsAccount.currency;

  const currencyError =
    currencySaveError
    || (currencyMismatch ? `Currency must match your Google Ads account (${googleAdsAccount?.currency}).` : '')
    || (!organizationData?.default_currency
        ? `Currency is required${suggestedCurrency ? `. Your Google Ads account uses ${suggestedCurrency}.` : '.'}`
        : '')
    || null;

  const isCurrencyDirty = !organizationData?.default_currency
    || currencyMismatch
    || (!!pendingCurrency && pendingCurrency !== organizationData?.default_currency);
  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty: isCurrencyDirty,
    // handleOrgClick awaits switchOrganization (swaps the active org) BEFORE
    // navigate, so the click that lands on a mismatched org would otherwise
    // block on arrival. Scope the guard to the CURRENT org's own page: landing
    // on it and tab-switching within it are free; leaving it (to another
    // section) while mismatched is what blocks.
    allowedBasePath: organizationData?.id ? `/organizations/${organizationData.id}` : '/organizations',
    onSave: async () => {
      const currencyToSave = displayedCurrency;
      if (!organizationData) return;
      if (googleAdsAccount?.currency && currencyToSave !== googleAdsAccount.currency) {
        throw new Error(`Currency must match your Google Ads account (${googleAdsAccount.currency}).`);
      }
      try {
        await updateCurrency.mutateAsync({ organizationId: organizationData.id, currency: currencyToSave });
      } catch (error) {
        throw new Error(currencySaveFailure(error));
      }
      setOrganizationData({ ...organizationData, default_currency: currencyToSave });
      await invalidateQuery(['organizations']);
      setPendingCurrency(null);
    },
    onDiscard: () => { setPendingCurrency(null); setCurrencySaveError(null); },
    isSaving: updateCurrency.isPending,
    contextName: 'Currency',
    saveBlockedReason: (() => {
      const c = displayedCurrency;
      return (googleAdsAccount?.currency && c && c !== googleAdsAccount.currency)
        ? `Currency must match your Google Ads account (${googleAdsAccount.currency}).`
        : null;
    })(),
  });

  // Get org ID from URL params (reactive to route changes)
  const urlOrgId = params.orgId ? parseInt(params.orgId, 10) : null;

  // Selected org is from URL, or fallback to current org
  const selectedOrgId = urlOrgId ?? organizationData?.id ?? null;

  const { activeTab, handleTabChange } = useTabPersistence(
    selectedOrganizationsTabAtom,
    TAB_IDS,
    'overview',
    (tabId, replace) => {
      if (selectedOrgId) {
        navigate({ to: '/organizations/$orgId', params: { orgId: selectedOrgId.toString() }, hash: tabId, replace });
      }
    },
    location.hash,
  );

  useEffect(() => {
    posthog?.capture('organizations_page_viewed');
  }, [posthog]);

  // Redirect to URL with org ID if not present (only on initial load)
  useEffect(() => {
    if (organizationData?.id && !params.orgId) {
      navigate({
        to: '/organizations/$orgId',
        params: { orgId: organizationData.id.toString() },
        hash: 'overview',
        replace: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData?.id, params.orgId]);

  if (!organizations || organizations.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-900">Loading organizations...</p>
        </div>
      </div>
    );
  }

  const selectedOrg = organizations.find(org => org.id === selectedOrgId);
  const isCurrentOrg = selectedOrgId === organizationData?.id;
  // Use organizationData (atom, always fresh after switch) for current org role,
  // fall back to organizations query cache for other orgs
  const isOwner = isCurrentOrg
    ? organizationData?.membership?.role?.toLowerCase() === 'owner'
    : selectedOrg?.membership?.role?.toLowerCase() === 'owner';

  const handleOrgClick = async (org: OrganizationDataWithMembership) => {
    setIsEditingName(false);

    // Switch to the organization if it's not the current one
    const isCurrent = org.id === organizationData?.id;
    if (!isCurrent && !isSwitching) {
      await switchOrganization(org);
    }

    // Navigate to org URL, preserving current tab (hash)
    navigate({
      to: '/organizations/$orgId',
      params: { orgId: org.id!.toString() },
      hash: activeTab
    });
  };

  // Handle clicking campaigns link - switch org and navigate
  const handleCampaignsClick = async (org: OrganizationDataWithMembership) => {
    // Navigate immediately, then switch org if needed
    // This ensures navigation isn't blocked by the switch process
    if (org.id !== organizationData?.id) {
      // Switch org in background - navigation will show the new org's campaigns
      switchOrganization(org);
    }
    navigate({ to: '/campaigns' });
  };

  // Fetch campaigns for the currently selected organization (source for transfer)
  const { data: campaignsData = [], isLoading: isLoadingCampaigns } = useQuery({
    queryKey: ['transfer-campaigns', selectedOrg?.id],
    queryFn: () => fetchCampaigns(selectedOrg!.id),
    enabled: !!selectedOrg?.id,
  });

  const transferableCampaigns = useMemo(() => campaignsData || [], [campaignsData]);

  // Reset transfer state when selected org changes
  useEffect(() => {
    setTargetOrganizationId('');
    setSelectedCampaignIds([]);
  }, [selectedOrgId]);

  const getStateDisplay = (campaign: CampaignData) => {
    switch (campaign.state) {
      case CAMPAIGN_STATE.LIVE:
        return { text: 'Live', color: 'bg-green-100 text-green-800' };
      case CAMPAIGN_STATE.PAUSED:
        return { text: campaign.first_published_at ? 'Paused' : 'Ready', color: 'bg-amber-100 text-amber-800' };
      case CAMPAIGN_STATE.DRAFT:
        return { text: 'Ready', color: 'bg-amber-100 text-amber-800' };
      default:
        return { text: campaign.state, color: 'bg-gray-100 text-gray-900' };
    }
  };

  const handleSelectAll = () => {
    if (selectedCampaignIds.length === transferableCampaigns.length) {
      setSelectedCampaignIds([]);
    } else {
      setSelectedCampaignIds(transferableCampaigns.map(c => c.id));
    }
  };

  const handleCampaignToggle = (campaignId: number) => {
    setSelectedCampaignIds(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleTransfer = async () => {
    if (!selectedOrg || selectedCampaignIds.length === 0 || !targetOrganizationId) return;

    try {
      await transferMutation.mutateAsync({
        campaignIds: selectedCampaignIds,
        targetOrganizationId: parseInt(targetOrganizationId),
        sourceOrganizationId: selectedOrg.id!,
      });

      toast({
        description: `${selectedCampaignIds.length} campaign(s) have been transferred successfully.`,
      });

      // Reset state
      setSelectedCampaignIds([]);
      setTargetOrganizationId('');
      invalidateQuery(['organizations']);
      invalidateQuery(['transfer-campaigns', selectedOrg?.id]);
    } catch {
      toast({
        description: 'Failed to transfer campaigns',
        variant: 'destructive',
      });
    }
  };

  const isAllSelected = selectedCampaignIds.length === transferableCampaigns.length && transferableCampaigns.length > 0;
  const isIndeterminate = selectedCampaignIds.length > 0 && selectedCampaignIds.length < transferableCampaigns.length;
  const canTransfer = selectedCampaignIds.length > 0 && targetOrganizationId && selectedOrg;

  const handleEditName = () => {
    if (selectedOrg?.name) {
      setEditedName(selectedOrg.name);
      setEditedNameError(undefined);
      setIsEditingName(true);
    }
  };

  const handleSaveName = async () => {
    if (!selectedOrg) return;
    if (!editedName.trim()) {
      setEditedNameError('Required');
      return;
    }

    try {
      await updateOrganization.mutateAsync({ id: selectedOrg.id, name: editedName.trim() });

      setIsEditingName(false);
      invalidateQuery(['organizations']);
    } catch {
      toast({
        description: 'Failed to update organization name',
        variant: 'destructive',
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName('');
    setEditedNameError(undefined);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>

      {/* Main Layout: Organizations List (3/12) + Details Panel (9/12) */}
      <div className="grid grid-cols-12 gap-1">
        {/* Organizations List - Left Side (3/12) */}
        <div className="col-span-3">
          <TabCardPanel
            tabs={[
              { id: 'orgs', label: 'Organizations', isActive: true },
              { id: 'new', label: '+ Add', onClick: () => navigate({ to: '/welcome' }) },
            ]}
            cardClassName="overflow-hidden"
            noPadding
          >
              <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
                <nav>
                  {organizations.map(org => {
                    const isSelected = selectedOrgId === org.id;

                    return (
                      <div
                        key={org.id}
                        onClick={() => handleOrgClick(org)}
                        className={`m-1 rounded-md transition-colors cursor-pointer ${
                          isSelected ? 'bg-[#290BD9]/10' : 'hover:bg-gray-50'
                        } group`}
                      >
                        <div className="p-2 flex items-center gap-2">
                          {/* Left icon */}
                          <Building2 className="h-4 w-4 text-gray-700 flex-shrink-0" />
                          {/* Middle content */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-gray-900 truncate">{org.name}</span>
                              {isSelected && (
                                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                                  Selected
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center mt-0.5">
                              <span>{org.user_count || 0} members</span>
                              <span className="mx-1">•</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCampaignsClick(org);
                                }}
                                className="text-[#290BD9] hover:underline"
                              >
                                {org.campaign_count || 0} campaigns
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </nav>
              </div>
          </TabCardPanel>
        </div>

        {/* Details Panel - Right Side (9/12) */}
        <div className="col-span-9">
          {selectedOrg ? (
            <>
              <TabCardPanel
                tabs={[
                  { id: 'overview', label: 'Overview', isActive: activeTab === 'overview', onClick: () => handleTabChange('overview') },
                  { id: 'members', label: 'Members', isActive: activeTab === 'members', onClick: () => handleTabChange('members') },
                  { id: 'providers', label: 'Providers', isActive: activeTab === 'providers', onClick: () => handleTabChange('providers') },
                  ...(isOwner ? [{ id: 'billing', label: 'Billing', isActive: activeTab === 'billing', onClick: () => handleTabChange('billing') }] : []),
                  ...(isCurrentOrg ? [{ id: 'configuration', label: 'Configuration', isActive: activeTab === 'configuration', onClick: () => handleTabChange('configuration') }] : []),
                  { id: 'transfer', label: 'Transfer', isActive: activeTab === 'transfer', onClick: () => handleTabChange('transfer') },
                ]}
                actions={
                  <>
                    {activeTab === 'overview' && (currencySaveSuccess || isCurrencyDirty) && (
                      <div className="flex items-center gap-3">
                        {/* One control owns both states: it renders "Saved ✓" in place of the button
                            once the save lands, never beside it (#5347). */}
                        <SaveChangesButton
                          saving={updateCurrency.isPending}
                          saved={currencySaveSuccess && !isCurrencyDirty}
                          className="h-9 shadow-lg animate-bounce-in-right"
                          onClick={async () => {
                            if (!organizationData) return;
                            const currencyToSave = displayedCurrency;
                            // Validate against connected Google Ads account
                            if (googleAdsAccount?.currency && currencyToSave !== googleAdsAccount.currency) {
                              setCurrencySaveError(
                                `Currency must match your Google Ads account (${googleAdsAccount.currency}).`
                              );
                              setPendingCurrency(null);
                              return;
                            }
                            setCurrencySaveError(null);
                            try {
                              await updateCurrency.mutateAsync({ organizationId: organizationData.id, currency: currencyToSave });
                              setOrganizationData({ ...organizationData, default_currency: currencyToSave });
                              await invalidateQuery(['organizations']);
                              setPendingCurrency(null);
                              setCurrencySaveSuccess(true);
                              setTimeout(() => setCurrencySaveSuccess(false), 2000);
                            } catch (error) {
                              toast({ description: currencySaveFailure(error), variant: 'destructive' });
                            }
                          }}
                        />
                      </div>
                    )}
                    {activeTab === 'members' && (
                      <Button
                        variant="outline"
                        className="text-gray-900"
                        onClick={() => setIsInviteModalOpen(true)}
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Invite
                      </Button>
                    )}
                    {activeTab === 'providers' && isCurrentOrg && (
                      <Button
                        variant="outline"
                        className="text-gray-900"
                        onClick={() => setIsGoogleAdsModalOpen(true)}
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Switch Account
                      </Button>
                    )}
                  </>
                }
                contentClassName={activeTab === 'members' || activeTab === 'providers' ? 'p-0' : 'p-6'}
                noPadding
              >
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="space-y-8">
                      {/* Organization Name Section */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h2 className="text-xl font-semibold text-gray-900">Organization</h2>
                            <p className="text-sm text-gray-600 mt-1">Manage your organization details.</p>
                          </div>
                        </div>
                        <div className="border rounded-lg bg-white overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <tbody className="bg-white divide-y divide-gray-200">
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap w-32">
                                  <span className="text-sm font-medium text-gray-500">Name</span>
                                </td>
                                <td className="px-6 py-4">
                                  {isEditingName ? (
                                    <div className="flex items-center gap-2">
                                      <div className="relative max-w-xs w-full">
                                        <Input
                                          value={editedName}
                                          onChange={(e) => {
                                            setEditedName(e.target.value);
                                            setEditedNameError(undefined);
                                          }}
                                          autoFocus
                                          error={!!editedNameError}
                                          className={editedNameError ? 'pr-8' : ''}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveName();
                                            if (e.key === 'Escape') handleCancelEdit();
                                          }}
                                        />
                                        <InputAdornment error={!!editedNameError} />
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={handleSaveName}
                                        disabled={editedName.trim() === selectedOrg?.name}
                                      >
                                        <Check className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={handleCancelEdit}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                      <FieldError message={editedNameError} />
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-900">{selectedOrg.name}</span>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={handleEditName}
                                        className="text-gray-500 hover:text-gray-900"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm font-medium text-gray-500">Plan</span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-900 capitalize">{selectedOrg.plan_type || 'Free'}</span>
                                    {selectedOrg.plan_type === PlanType.FREE && isCurrentOrg && (
                                      <button
                                        onClick={() => requirePayment(() => {})}
                                        className="text-sm text-[#290BD9] hover:underline"
                                      >
                                        Upgrade
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm font-medium text-gray-500">Your Role</span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-gray-900 capitalize">{selectedOrg.membership?.role || 'Member'}</span>
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm font-medium text-gray-500">Members</span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-gray-900">{selectedOrg.user_count || 0}</span>
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm font-medium text-gray-500">Campaigns</span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-gray-900">{selectedOrg.campaign_count || 0}</span>
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm font-medium text-gray-500">Google Ads</span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <img src={googleAds} alt="Google Ads" className="w-4 h-4" />
                                    <span className="text-sm text-gray-900">
                                      {selectedOrg.membership?.last_selected_googleads_account_name || 'Not connected'}
                                    </span>
                                    {selectedOrg.membership?.last_selected_googleads_account_name && (
                                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                                        Connected
                                      </Badge>
                                    )}
                                    {isCurrentOrg && (
                                      <button
                                        onClick={() => setIsGoogleAdsModalOpen(true)}
                                        className="text-sm text-[#290BD9] hover:underline"
                                      >
                                        {selectedOrg.membership?.last_selected_googleads_account_name ? 'Switch' : 'Connect'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm font-medium text-gray-500">Meta</span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <img src={metaLogo} alt="Meta" className="w-4 h-4 object-contain" />
                                    <span className="text-sm text-gray-900">
                                      {/* Meta integration status is current-org only; a non-current org can't show its
                                          account name (membership carries no Meta name, unlike Google Ads), so it reads
                                          "Not connected" rather than risk showing the current org's account. */}
                                      {(isCurrentOrg && metaIntegration?.selected_ad_account?.name) || 'Not connected'}
                                    </span>
                                    {isCurrentOrg && metaIntegration?.selected_ad_account?.name && (
                                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                                        Connected
                                      </Badge>
                                    )}
                                    {isCurrentOrg && (
                                      <button
                                        onClick={() => setIsMetaModalOpen(true)}
                                        className="text-sm text-[#290BD9] hover:underline"
                                      >
                                        {metaIntegration?.selected_ad_account?.name ? 'Switch' : 'Connect'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td className="px-6 py-2.5 whitespace-nowrap">
                                  <span className="text-sm font-medium text-gray-500">Currency</span>
                                </td>
                                <td className="px-6 py-2.5">
                                  {isCurrentOrg ? (
                                    <div className="flex items-center gap-3">
                                      <Select
                                        value={displayedCurrency}
                                        onValueChange={(value) => { setPendingCurrency(value); setCurrencySaveError(null); }}
                                      >
                                        <SelectTrigger className="w-[160px] h-8 text-sm">
                                          <SelectValue placeholder="Select currency" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {CURRENCY_OPTIONS.filter((c) => (SHORTCUT_CURRENCIES as readonly string[]).includes(c.code)).map((c) => (
                                            <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                                          ))}
                                          <SelectSeparator />
                                          {CURRENCY_OPTIONS.filter((c) => !(SHORTCUT_CURRENCIES as readonly string[]).includes(c.code)).map((c) => (
                                            <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      {currencyError && (
                                        <span className="flex items-center gap-1.5 text-sm text-[#B91C1C]">
                                          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                                          <span>{currencyError}</span>
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-900">{selectedOrg?.default_currency || 'Not set'}</span>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Providers Tab */}
                  {activeTab === 'providers' && (
                    <GoogleAdsPermissions />
                  )}

                  {/* Billing Tab (Owner only) */}
                  {activeTab === 'billing' && isOwner && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">Billing</h2>
                          <p className="text-sm text-gray-600 mt-1">Manage your subscription and billing details.</p>
                        </div>
                      </div>
                      <BiilingInfo />
                    </div>
                  )}

                  {/* Members Tab */}
                  {activeTab === 'members' && (
                    <MembersTable
                      organizationId={selectedOrg?.id}
                    />
                  )}

                  {/* Transfer Tab */}
                  {activeTab === 'configuration' && isCurrentOrg && selectedOrgId && (
                    <BusinessAiConfiguration orgId={selectedOrgId} />
                  )}

                  {activeTab === 'transfer' && (
                    <div className="space-y-6">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">Transfer Campaigns</h2>
                          <p className="text-sm text-gray-600 mt-1">
                            Transfer campaigns from {selectedOrg?.name}
                            {isLoadingCampaigns ? '' : ` (${transferableCampaigns.length} campaigns)`}
                          </p>
                        </div>
                      </div>

                      {/* Empty state when no campaigns */}
                      {!isLoadingCampaigns && transferableCampaigns.length === 0 && (
                        <EmptyState
                          icon={<ArrowLeftRight className="h-12 w-12 text-gray-500" strokeWidth={1} />}
                          title="No Campaigns to Transfer"
                          description="This organization has no campaigns that can be transferred."
                          className="border-0 shadow-none"
                        />
                      )}

                      {/* Campaign Selection Table */}
                      {!isLoadingCampaigns && transferableCampaigns.length > 0 && (
                        <>
                          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-white sticky top-0">
                                  <tr>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                                      <Checkbox
                                        checked={isAllSelected}
                                        ref={(ref) => {
                                          if (ref) (ref as unknown as HTMLInputElement).indeterminate = isIndeterminate;
                                        }}
                                        onCheckedChange={handleSelectAll}
                                      />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {transferableCampaigns.map((campaign) => {
                                    const isSelected = selectedCampaignIds.includes(campaign.id);
                                    const stateDisplay = getStateDisplay(campaign);
                                    const campaignType = getCampaignType(campaign);
                                    const displayName = campaign.name;

                                    return (
                                      <tr
                                        key={campaign.id}
                                        className={`cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-[#290BD9]/10' : ''}`}
                                        onClick={() => handleCampaignToggle(campaign.id)}
                                      >
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                          <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={() => handleCampaignToggle(campaign.id)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          <div className="flex items-center space-x-3">
                                            <img src={googleAds} alt="Google Ads" className="w-5 h-5 flex-shrink-0" />
                                            <span className="font-medium text-gray-900">{displayName}</span>
                                          </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                                          {getCampaignTypeLabel(campaignType)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          <span className={`inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium ${stateDisplay.color}`}>
                                            {stateDisplay.text}
                                          </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                                          {campaign.budget ? `${symbol}${campaign.budget}/d` : 'Not set'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Target Organization Selection */}
                          <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-900">
                              Transfer {selectedCampaignIds.length > 0 ? `${selectedCampaignIds.length} campaign${selectedCampaignIds.length !== 1 ? 's' : ''}` : 'selected campaigns'} to
                            </label>
                            <div className="w-1/3">
                              <Select value={targetOrganizationId} onValueChange={setTargetOrganizationId}>
                                <SelectTrigger className="cursor-pointer">
                                  <Building2 className="h-4 w-4 mr-2" />
                                  <SelectValue placeholder="Select destination organization..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {organizations
                                    .filter(org => org.id !== selectedOrg?.id)
                                    .map((org) => (
                                      <SelectItem key={org.id} value={org.id!.toString()}>
                                        {org.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Transfer Button */}
                          <div className="flex justify-start">
                            <Button
                              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
                              onClick={handleTransfer}
                              disabled={!canTransfer || transferMutation.isPending}
                            >
                              {transferMutation.isPending ? 'Transferring...' : 'Transfer Campaigns'}
                              {!transferMutation.isPending && <ArrowRight className="h-4 w-4 ml-2" />}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
              </TabCardPanel>
            </>
          ) : (
            /* Empty State */
            <div className="mt-[42px]">
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                <Building2 className="mx-auto h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                <h3 className="text-lg font-semibold text-gray-500 mb-2">Select an Organization</h3>
                <p className="text-gray-500">
                  Click any organization in the list to view details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <GoogleAdAccountSelectionModal
        isOpen={isGoogleAdsModalOpen}
        onClose={() => setIsGoogleAdsModalOpen(false)}
      />

      <MetaAdAccountSelectionModal
        isOpen={isMetaModalOpen}
        onClose={() => setIsMetaModalOpen(false)}
      />

      {selectedOrg && (
        <InviteMemberModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          organizationId={selectedOrg.id!}
        />
      )}

      <PaymentGate />
      {UnsavedChangesModal}
    </div>
  );
}
